import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import * as Sentry from '@sentry/react';

import App from './App';
import { AuthProvider } from './context/AuthContext';

import './i18n';

if (import.meta.env.PROD) {
  console.log = () => {};
  console.debug = () => {};
}

const isSentryEnabled = import.meta.env.PROD;

if (isSentryEnabled) {
  Sentry.init({
    dsn: 'https://64f15f07d948b66a4430c985480028c1@o4510985474998272.ingest.de.sentry.io/4510985478406224',
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    sendDefaultPii: false,
    enableLogs: true,
    release: __APP_VERSION__,
    environment: __APP_VERSION__.includes('rc') ? 'staging' : 'production',
  });
}

const domNode = document.getElementById('root') as HTMLElement;

const root = createRoot(domNode, {
  // Callback called when an error is thrown and not caught by an Error Boundary.
  onUncaughtError: isSentryEnabled
    ? Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Uncaught error', error, errorInfo.componentStack);
      })
    : undefined,
  // Callback called when React automatically recovers from errors.
  onRecoverableError: isSentryEnabled ? Sentry.reactErrorHandler() : undefined,
});

root.render(
  <StrictMode>
    {isSentryEnabled ? (
      <Sentry.ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </Sentry.ErrorBoundary>
    ) : (
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    )}
  </StrictMode>,
);
